import { useCallback, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiChip, MiIcon } from "@miplatform/components";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
} from "@mui/material";
import type { Palette } from "@mui/material/styles";

type CalendarTabBadgeTone = "unassigned" | "notReady" | "assigned";

export type TabGroupItem = {
  label: string;
  value: string;
  count: number;
};

export type TabGroup = {
  label: string;
  items: readonly TabGroupItem[];
  badgeTone: CalendarTabBadgeTone;
};

type CalendarVerticalTabsProps<T extends string> = {
  groups: Record<T, TabGroup>;
  defaultGroup: T;
  onSelectionChange: (group: T, item: string | null) => void;
  onSubItemPress?: (group: T, item: TabGroupItem) => void;
};

type VerticalTabStyles = ReturnType<typeof useStyles>["classes"];
type VerticalTabCx = ReturnType<typeof useStyles>["cx"];

function calendarTabBadgePalette(
  tone: CalendarTabBadgeTone,
  palette: Palette
): {
  backgroundColor?: string;
  color?: string;
} {
  switch (tone) {
    case "unassigned":
      return {
        backgroundColor: palette.error.main,
        color: palette.common.white,
      };
    case "notReady":
      return {
        backgroundColor: palette.warning.main,
        color: palette.common.white,
      };
    case "assigned":
      return {
        backgroundColor: palette.grey.medium,
        color: palette.common.white,
      };
    default:
      return {
        backgroundColor: palette.grey.main,
        color: palette.common.white,
      };
  }
}

export function CalendarVerticalTabs<T extends string>({
  groups,
  defaultGroup,
  onSelectionChange,
  onSubItemPress,
}: Readonly<CalendarVerticalTabsProps<T>>) {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const [expandedGroups, setExpandedGroups] = useState<Set<T>>(
    () => new Set(Object.keys(groups) as T[])
  );
  const [selectedGroup, setSelectedGroup] = useState<T>(defaultGroup);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const toggleGroup = useCallback((e: React.MouseEvent, groupKey: T) => {
    e.stopPropagation();
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const handleGroupSelect = useCallback(
    (groupKey: T) => {
      setSelectedGroup(groupKey);
      setSelectedItem(null);
      onSelectionChange(groupKey, null);
    },
    [onSelectionChange]
  );

  const handleItemSelect = useCallback(
    (groupKey: T, item: TabGroupItem) => {
      setSelectedGroup(groupKey);
      setSelectedItem(item.value);
      onSelectionChange(groupKey, item.value);
      onSubItemPress?.(groupKey, item);
    },
    [onSelectionChange, onSubItemPress]
  );

  const countChipSx = useCallback(
    (tone: CalendarTabBadgeTone) => {
      const p = calendarTabBadgePalette(tone, theme.palette);
      return {
        backgroundColor: p.backgroundColor,
        color: p.color,
        height: "auto",
        "& .MuiChip-label": {
          fontSize: 11,
        },
      };
    },
    [theme.palette]
  );

  const groupsEntries = useMemo(
    () => Object.entries(groups) as [T, TabGroup][],
    [groups]
  );

  return (
    <List component="nav" className={classes.sidebar} disablePadding>
      {groupsEntries.map(([groupKey, group], groupIndex) => {
        const isGroupSelected =
          selectedGroup === groupKey && selectedItem === null;
        const isExpanded = expandedGroups.has(groupKey);

        return (
          <Box
            key={groupKey}
            className={cx(groupIndex > 0 && classes.groupSectionDivider)}
          >
            <GroupHeader
              group={group}
              isExpanded={isExpanded}
              isSelected={isGroupSelected}
              classes={classes}
              cx={cx}
              onSelect={() => handleGroupSelect(groupKey)}
              onToggle={(e) => toggleGroup(e, groupKey)}
              countChipSx={countChipSx}
            />
            <GroupItems
              group={group}
              isExpanded={isExpanded}
              selectedItem={selectedItem}
              isGroupSelected={selectedGroup === groupKey}
              classes={classes}
              cx={cx}
              onItemSelect={(item) => handleItemSelect(groupKey, item)}
              countChipSx={countChipSx}
            />
          </Box>
        );
      })}
    </List>
  );
}

type GroupHeaderProps = {
  group: TabGroup;
  isExpanded: boolean;
  isSelected: boolean;
  classes: VerticalTabStyles;
  cx: VerticalTabCx;
  onSelect: () => void;
  onToggle: (e: React.MouseEvent) => void;
  countChipSx: (tone: CalendarTabBadgeTone) => {
    backgroundColor?: string;
    color?: string;
    height: string;
    "& .MuiChip-label": {
      fontSize: number;
    };
  };
};

function GroupHeader({
  group,
  isExpanded,
  isSelected,
  classes,
  cx,
  onSelect,
  onToggle,
  countChipSx,
}: Readonly<GroupHeaderProps>) {
  const groupTotalCount = getGroupTotalCount(group.items);

  return (
    <ListItemButton
      selected={isSelected}
      onClick={onSelect}
      className={cx(classes.groupHeader, {
        [classes.selectedStyle]: isSelected,
      })}
    >
      <IconButton size="small" onClick={onToggle} edge="start">
        <MiIcon path={isExpanded ? mdiChevronUp : mdiChevronDown} size={0.8} />
      </IconButton>
      <Box className={classes.row}>
        <ListItemText
          primary={group.label}
          primaryTypographyProps={{
            fontSize: 12,
          }}
        />
        <CountBadge
          count={groupTotalCount}
          tone={group.badgeTone}
          classes={classes}
          countChipSx={countChipSx}
        />
      </Box>
    </ListItemButton>
  );
}

type GroupItemsProps = {
  group: TabGroup;
  isExpanded: boolean;
  isGroupSelected: boolean;
  selectedItem: string | null;
  classes: VerticalTabStyles;
  cx: VerticalTabCx;
  onItemSelect: (item: TabGroupItem) => void;
  countChipSx: (tone: CalendarTabBadgeTone) => {
    backgroundColor?: string;
    color?: string;
    height: string;
    "& .MuiChip-label": {
      fontSize: number;
    };
  };
};

function GroupItems({
  group,
  isExpanded,
  isGroupSelected,
  selectedItem,
  classes,
  cx,
  onItemSelect,
  countChipSx,
}: Readonly<GroupItemsProps>) {
  return (
    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
      <List component="div" disablePadding>
        {group.items.map((item, itemIndex) => {
          const isItemSelected = isGroupSelected && selectedItem === item.value;

          return (
            <ListItemButton
              key={item.value}
              selected={isItemSelected}
              onClick={() => onItemSelect(item)}
              className={cx(classes.nestedItem, {
                [classes.selectedStyle]: isItemSelected,
                [classes.nestedItemDivider]: itemIndex > 0,
              })}
            >
              <Box className={classes.row}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 12 }}
                />
                <CountBadge
                  count={item.count}
                  tone={group.badgeTone}
                  classes={classes}
                  countChipSx={countChipSx}
                />
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </Collapse>
  );
}

type CountBadgeProps = {
  count: number;
  tone: CalendarTabBadgeTone;
  classes: VerticalTabStyles;
  countChipSx: (tone: CalendarTabBadgeTone) => {
    backgroundColor?: string;
    color?: string;
    height: string;
    "& .MuiChip-label": {
      fontSize: number;
    };
  };
};

function CountBadge({
  count,
  tone,
  classes,
  countChipSx,
}: Readonly<CountBadgeProps>) {
  if (count <= 0) {
    return null;
  }

  return (
    <MiChip
      size="small"
      className={classes.tabCountBadge}
      label={String(count)}
      sx={countChipSx(tone)}
    />
  );
}

function getGroupTotalCount(items: readonly TabGroupItem[]): number {
  return items.reduce((sum, item) => sum + item.count, 0);
}

const useStyles = makeStyles()((theme) => ({
  sidebar: {
    minWidth: 220,
    overflowY: "auto",
  },
  groupSectionDivider: {
    borderTop: `1px solid ${theme.palette.grey.lightAccent}`,
  },
  groupHeader: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  selectedStyle: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      borderRadius: theme.spacing(1),
    },
  },
  nestedItem: {
    paddingLeft: theme.spacing(11),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  nestedItemDivider: {
    borderTop: `1px solid ${theme.palette.grey.light}`,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    "& .MuiListItemText-root": {
      flex: "1 1 auto",
      minWidth: 0,
    },
  },
  tabCountBadge: {
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 400,
  },
}));
